import { browser, dev } from '$app/environment';
import { replaceState } from '$app/navigation';
import { page } from '$app/state';
import { scrollLock } from '$lib/utils/scrollLock.svelte';
import type { EppoData, GlobalCartData } from '$lib/storyblok/types';
import { storyblok } from './storyblok.svelte';
import { checkoutSession } from './checkoutSession.svelte';
import type { CartProductAddon } from '$lib/storyblok/blocks/cart/cartProductAddon.svelte';
import type { ProductDetails } from '$lib/storyblok/blocks/cart/productDetails.svelte';
import log from '$lib/utils/log';
import { PUBLIC_APP_ENV } from '$env/static/public';

const PARTNER_URL_PARAMS = ['mill_partner_source', 'mill_pilot_source', 'mill_promo_code'];

function getUrlCouponCode(params: URLSearchParams): string | undefined {
	for (const param of PARTNER_URL_PARAMS) {
		const value = params.get(param);
		if (value && value.length > 0) {
			return value;
		}
	}
}

function useIfNotEmpty<T>(newValue: T, currentValue: T) {
	if (typeof newValue === 'string' || Array.isArray(newValue)) {
		return newValue.length > 0 ? newValue : currentValue;
	}

	return newValue ?? currentValue;
}

type BundleProducts = ProductDetails | CartProductAddon | EppoData;

interface ProductBundle {
	products: Array<BundleProducts>;
	configuration: Array<GlobalCartData>;
	show_selected_only: boolean;
}

// @internal
export class ShoppingCart {
	static CART_PARAM = 'cart';

	#productBundle = $state<ProductBundle | null>(null);
	#current = $state<GlobalCartData | null>(null);

	#bundleCache: Record<string, ProductBundle> = {};

	#initPromise: Promise<void> | null = null;
	#updating = $state(false);

	get isUpdating() {
		return this.#updating;
	}

	get data() {
		return this.#current;
	}

	get components() {
		return this.#productBundle?.products.filter(
			(product) =>
				product.component !== 'cart_product_addon' ||
				!product.base_product ||
				product.base_product.priceLookupKey === checkoutSession.selectedLookupKey ||
				product.addon_product?.priceLookupKey === checkoutSession.selectedLookupKey
		);
	}

	showProductSelectors = $derived(
		!this.#productBundle?.show_selected_only &&
			this.products &&
			this.products.filter((p) => p.component === 'product_details').length > 1
	);

	get products() {
		return this.#productBundle?.products
			? this.#productBundle.products.flatMap((p) => {
					if (p.component === 'eppo_variant') {
						return p.eppo.assignment.variation === p.eppo?.variant.key
							? (p.content as unknown as Array<ProductDetails | CartProductAddon>)
							: [];
					}
					return p;
				})
			: [];
	}

	async #getBundle(bundleId: string) {
		if (this.#bundleCache[bundleId]) {
			return this.#bundleCache[bundleId];
		}

		const { data } = await storyblok.client.getStory<ProductBundle>(`cart/products/${bundleId}`, {
			resolve_relations: [
				'cart_configuration.success_page',
				'product_details.custom_order_success_page'
			]
		});

		this.#bundleCache[bundleId] = data.story.content;

		return this.#bundleCache[bundleId];
	}

	async #setCurrentData(data: GlobalCartData) {
		this.#current = data;

		await checkoutSession.configure({
			accessCode: this.#current?.partner_source,
			isTrial: this.#current?.is_trial ?? false,
			userLabels: this.#current?.user_label ? new Set([this.#current.user_label]) : new Set(),
			afterSubmitText: this.#current?.after_submit_text,
			productBundle: this.#current?.default_product_bundle,
			shippingId:
				PUBLIC_APP_ENV === 'prod' ? this.#current?.shipping_id : this.#current?.shipping_id_stage,
			successPage: this.#current?.success_page?.full_slug,
			termsOfService: this.#current?.terms_of_service_text,
			adjustableQuantity: {
				enabled: this.#current?.enable_quantity_selection ?? false,
				max: this.#current?.max_quantity ? parseInt(this.#current.max_quantity, 10) : undefined
			}
		});

		if (this.#current?.global_coupon_code) {
			await checkoutSession.applyCoupon(this.#current.global_coupon_code);
		}
	}

	async #setCurrent(data: GlobalCartData) {
		this.#updating = true;
		try {
			await this.#initPromise;
			await this.#setCurrentData(data);
		} catch (e) {
			log.scope('ShoppingCart').error('Error setting current', { error: e });
		} finally {
			this.#updating = false;
		}
	}

	#isOpen = $state(false);

	async #init() {
		await storyblok.isInitialized;

		const [config, bundle] = await Promise.all([
			storyblok.client.getStory<GlobalCartData>('cart/configuration'),
			this.#current?.default_product_bundle
				? this.#getBundle(this.#current.default_product_bundle)
				: null
		]);

		const configData = config.data.story.content;
		this.#productBundle = bundle;

		await this.#setCurrentData({
			...configData,
			...{
				success_page: useIfNotEmpty(this.#bundleConfig.success_page, configData.success_page),
				terms_of_service_text: useIfNotEmpty(
					this.#bundleConfig.terms_of_service_text,
					configData.terms_of_service_text
				),
				user_label: useIfNotEmpty(this.#bundleConfig.user_label, configData.user_label),
				partner_source: useIfNotEmpty(this.#bundleConfig.partner_source, configData.partner_source),
				shipping_id: useIfNotEmpty(this.#bundleConfig.shipping_id, configData.shipping_id),
				after_submit_text: useIfNotEmpty(
					this.#bundleConfig.after_submit_text,
					configData.after_submit_text
				),
				is_trial: this.#bundleConfig.is_trial ?? configData.is_trial,
				enable_quantity_selection:
					this.#bundleConfig.enable_quantity_selection ?? configData.enable_quantity_selection,
				max_quantity: useIfNotEmpty(this.#bundleConfig.max_quantity, configData.max_quantity),
				default_product_bundle: useIfNotEmpty(
					this.#bundleConfig.default_product_bundle,
					configData.default_product_bundle
				),
				cta_label: useIfNotEmpty(this.#bundleConfig.cta_label, configData.cta_label),
				processing_label: useIfNotEmpty(
					this.#bundleConfig.processing_label,
					configData.processing_label
				),
				title: useIfNotEmpty(this.#bundleConfig.title, configData.title),
				default_stripe_product: useIfNotEmpty(
					this.#bundleConfig.default_stripe_product,
					configData.default_stripe_product
				),
				display_cta_lock_icon:
					this.#bundleConfig.display_cta_lock_icon ?? configData.display_cta_lock_icon,
				display_discount: this.#bundleConfig.display_discount ?? configData.display_discount,
				display_shipping: this.#bundleConfig.display_shipping ?? configData.display_shipping,
				display_taxes: this.#bundleConfig.display_taxes ?? configData.display_taxes,
				display_affirm: this.#bundleConfig.display_affirm ?? configData.display_affirm,
				global_coupon_code: useIfNotEmpty(
					this.#bundleConfig.global_coupon_code,
					configData.global_coupon_code
				),
				original_shipping_value: useIfNotEmpty(
					this.#bundleConfig.original_shipping_value,
					configData.original_shipping_value
				),
				price_subline: useIfNotEmpty(this.#bundleConfig.price_subline, configData.price_subline),
				shipping_id_stage: useIfNotEmpty(
					this.#bundleConfig.shipping_id_stage,
					configData.shipping_id_stage
				),
				shipping_text: useIfNotEmpty(this.#bundleConfig.shipping_text, configData.shipping_text),
				// blocks are not merged
				body: this.#bundleConfig.body ?? [],
				below_cta_content: this.#bundleConfig.below_cta_content ?? []
			}
		});
	}

	get #bundleConfig() {
		return this.#productBundle?.configuration?.[0] ?? {};
	}

	async loadBundle(bundleId: string) {
		this.#productBundle = await this.#getBundle(bundleId);

		if (!this.#current) {
			await this.#setCurrentData(this.#bundleConfig);
			return;
		}

		await this.#setCurrentData({
			...this.#current,
			...this.#bundleConfig,
			success_page: useIfNotEmpty(this.#bundleConfig.success_page, this.#current.success_page),
			terms_of_service_text: useIfNotEmpty(
				this.#bundleConfig.terms_of_service_text,
				this.#current.terms_of_service_text
			),
			user_label: useIfNotEmpty(this.#bundleConfig.user_label, this.#current.user_label),
			partner_source: useIfNotEmpty(
				this.#bundleConfig.partner_source,
				this.#current.partner_source
			),
			shipping_id: useIfNotEmpty(this.#bundleConfig.shipping_id, this.#current.shipping_id),
			after_submit_text: useIfNotEmpty(
				this.#bundleConfig.after_submit_text,
				this.#current.after_submit_text
			),
			is_trial: this.#bundleConfig.is_trial ?? this.#current.is_trial,
			enable_quantity_selection:
				this.#bundleConfig.enable_quantity_selection ?? this.#current.enable_quantity_selection,
			max_quantity: useIfNotEmpty(this.#bundleConfig.max_quantity, this.#current.max_quantity),
			default_product_bundle: useIfNotEmpty(
				this.#bundleConfig.default_product_bundle,
				this.#current.default_product_bundle
			),
			cta_label: useIfNotEmpty(this.#bundleConfig.cta_label, this.#current.cta_label),
			processing_label: useIfNotEmpty(
				this.#bundleConfig.processing_label,
				this.#current.processing_label
			),
			title: useIfNotEmpty(this.#bundleConfig.title, this.#current.title),
			default_stripe_product: useIfNotEmpty(
				this.#bundleConfig.default_stripe_product,
				this.#current.default_stripe_product
			),
			display_cta_lock_icon:
				this.#bundleConfig.display_cta_lock_icon ?? this.#current.display_cta_lock_icon,
			display_discount: this.#bundleConfig.display_discount ?? this.#current.display_discount,
			display_shipping: this.#bundleConfig.display_shipping ?? this.#current.display_shipping,
			display_affirm: this.#bundleConfig.display_affirm ?? this.#current.display_affirm,
			display_taxes: this.#bundleConfig.display_taxes ?? this.#current.display_taxes,
			global_coupon_code: useIfNotEmpty(
				this.#bundleConfig.global_coupon_code,
				this.#current.global_coupon_code
			),
			original_shipping_value: useIfNotEmpty(
				this.#bundleConfig.original_shipping_value,
				this.#current.original_shipping_value
			),
			price_subline: useIfNotEmpty(this.#bundleConfig.price_subline, this.#current.price_subline),
			shipping_id_stage: useIfNotEmpty(
				this.#bundleConfig.shipping_id_stage,
				this.#current.shipping_id_stage
			),
			shipping_text: useIfNotEmpty(this.#bundleConfig.shipping_text, this.#current.shipping_text),
			// blocks are not merged
			body: this.#bundleConfig.body ?? [],
			below_cta_content: this.#bundleConfig.below_cta_content ?? []
		});
	}

	async merge(config: GlobalCartData) {
		this.#updating = true;

		try {
			if (
				config.force_bundle &&
				config.default_product_bundle !== checkoutSession.data.productBundle &&
				config.default_stripe_product
			) {
				if (dev) {
					log.scope('Checkout').debug('Forcing bundle', {
						bundle: config.default_product_bundle,
						product: config.default_stripe_product.priceLookupKey
					});
				}
				await checkoutSession.select(config.default_stripe_product, {
					productBundle: config.default_product_bundle,
					accessCode: config.partner_source,
					afterSubmitText: config.after_submit_text,
					isTrial: config.is_trial,
					shippingId: PUBLIC_APP_ENV === 'prod' ? config.shipping_id : config.shipping_id_stage,
					successPage: config.success_page?.full_slug,
					termsOfService: config.terms_of_service_text,
					userLabels:
						config.user_label && config.user_label.length > 0
							? new Set([config.user_label])
							: new Set(),
					previousLookupKey: undefined
				});
			} else if (config.default_product_bundle) {
				await this.loadBundle(config.default_product_bundle);

				if (
					config.force_product ||
					!this.products
						.map((p) =>
							p.component === 'product_details'
								? p.stripe_product?.priceLookupKey
								: p.addon_product?.priceLookupKey
						)
						.includes(checkoutSession.selectedLookupKey)
				) {
					let stripeProduct = config.default_stripe_product;

					if (!stripeProduct) {
						const firstProduct = this.products[0];

						if (firstProduct.component === 'product_details' && firstProduct.stripe_product) {
							stripeProduct = firstProduct.stripe_product;
						} else if (
							firstProduct.component === 'cart_product_addon' &&
							firstProduct.addon_product
						) {
							stripeProduct = firstProduct.addon_product;
						}
					}

					if (dev) {
						log.scope('Checkout').debug('Selected product not in bundle. Forcing bundle product.', {
							bundle: config.default_product_bundle,
							product: stripeProduct
						});
					}

					if (stripeProduct) {
						await checkoutSession.select(stripeProduct, {
							productBundle: config.default_product_bundle
						});
					} else {
						log.scope('Checkout').error('Cannot force bundle product. No product found.', {
							bundle: config.default_product_bundle,
							product: stripeProduct
						});
					}
				}
			}

			const current: Partial<GlobalCartData> = this.#current || {};

			await this.#setCurrent({
				force_bundle: config.force_bundle ?? current.force_bundle,
				success_page: useIfNotEmpty(config.success_page, current.success_page),
				terms_of_service_text: useIfNotEmpty(
					config.terms_of_service_text,
					current.terms_of_service_text
				),
				shipping_id: useIfNotEmpty(config.shipping_id, current.shipping_id),
				after_submit_text: useIfNotEmpty(config.after_submit_text, current.after_submit_text),
				is_trial: config.is_trial ?? current.is_trial,
				enable_quantity_selection:
					config.enable_quantity_selection ?? current.enable_quantity_selection,
				max_quantity: useIfNotEmpty(config.max_quantity, current.max_quantity),
				cta_label: useIfNotEmpty(config.cta_label, current.cta_label),
				processing_label: useIfNotEmpty(config.processing_label, current.processing_label),
				title: useIfNotEmpty(config.title, current.title),
				default_stripe_product: useIfNotEmpty(
					config.default_stripe_product,
					current.default_stripe_product
				),
				display_cta_lock_icon: config.display_cta_lock_icon ?? current.display_cta_lock_icon,
				display_discount: config.display_discount ?? current.display_discount ?? false,
				display_shipping: config.display_shipping ?? current.display_shipping ?? false,
				display_taxes: config.display_taxes ?? current.display_taxes ?? false,
				display_affirm: config.display_affirm ?? current.display_affirm ?? false,
				global_coupon_code: useIfNotEmpty(config.global_coupon_code, current.global_coupon_code),
				original_shipping_value: useIfNotEmpty(
					config.original_shipping_value,
					current.original_shipping_value
				),
				price_subline: useIfNotEmpty(config.price_subline, current.price_subline),
				shipping_id_stage: useIfNotEmpty(config.shipping_id_stage, current.shipping_id_stage),
				shipping_text: useIfNotEmpty(config.shipping_text, current.shipping_text),
				// prefer bundle config over global config
				default_product_bundle: useIfNotEmpty(
					config.default_product_bundle,
					this.#bundleConfig?.default_product_bundle
				),
				user_label: useIfNotEmpty(config.user_label, this.#bundleConfig?.user_label),
				partner_source: useIfNotEmpty(config.partner_source, this.#bundleConfig?.partner_source),
				body: useIfNotEmpty(config.body, this.#bundleConfig?.body ?? []),
				below_cta_content: useIfNotEmpty(
					config.below_cta_content,
					this.#bundleConfig?.below_cta_content ?? []
				)
			});
		} catch (e) {
			log.scope('ShoppingCart').error('Error merging config', { error: e });
		} finally {
			this.#updating = false;
		}
	}

	constructor() {
		if (!browser) return;

		this.#initPromise = this.#init();

		$effect.root(() => {
			$effect.pre(() => {
				const {
					url: { searchParams }
				} = page;

				if (searchParams.has(ShoppingCart.CART_PARAM)) {
					if (!this.isOpen) {
						this.#isOpen = true;
						scrollLock.lock();
						return;
					}
				}

				if (!searchParams.has(ShoppingCart.CART_PARAM) && this.#isOpen) {
					this.#isOpen = false;
					scrollLock.unlock();
					return;
				}
			});

			$effect.pre(() => {
				const {
					url: { searchParams }
				} = page;

				const couponCode = getUrlCouponCode(searchParams);
				if (couponCode) {
					checkoutSession.applyCoupon(couponCode);
				}
			});
		});
	}

	get isOpen() {
		return this.#isOpen;
	}

	async open() {
		this.#isOpen = true;
		scrollLock.lock();

		const { url, state } = page;

		url.searchParams.set(ShoppingCart.CART_PARAM, 'open');
		replaceState(`?${url.searchParams.toString()}`, state);
	}

	close() {
		this.#isOpen = false;
		scrollLock.unlock();

		const { url, state } = page;

		url.searchParams.delete(ShoppingCart.CART_PARAM);
		replaceState(
			url.searchParams.size > 0 ? `?${url.searchParams.toString()}` : window.location.pathname,
			state
		);
	}
}

export const shoppingCart = new ShoppingCart();
